/**
 * Influencer reels trigger block (product information area).
 *
 * Renders compact poster-image cards that — on click — open the modal
 * owned by the `<influencer-video-reels>` section that lives elsewhere
 * on the same page (typically at the bottom of the product template
 * with `show_cards` toggled off so the section's own carousel is
 * hidden). The card click dispatches the `influencer-reels:open`
 * CustomEvent on that section's element; the section's module
 * (module-influencer-video-reels.js) already listens for the event
 * and opens the modal at the requested slide index.
 *
 * Why a CustomEvent instead of importing the modal here? The section
 * already owns the slide JSON, modal config, and lifecycle. The
 * trigger block is intentionally lightweight — it only knows the
 * slide INDEX to open; the section retains its full data and
 * orchestration responsibilities.
 */

(function () {
  "use strict";

  const TRIGGER_SELECTOR = ".wt-ivr-trigger";
  const CARD_SELECTOR = ".wt-ivr-trigger__card";
  const SECTION_TAG = "influencer-video-reels";

  /*
    Tap-vs-swipe gate thresholds.

    The trigger block puts cards inside a horizontal Swiper that the
    user genuinely needs to swipe on mobile (their videos overflow
    the viewport). The previous implementation listened on
    `pointerdown` in capture phase, which opened the modal on the
    very first touch — before Swiper could even register a drag.
    That made swiping impossible: any finger contact with a card
    instantly opened the modal.

    The fix is a small homegrown tap detector. We don't rely on
    Swiper's internal `preventClicks` machinery because its timing
    depends on Swiper version and event-listener ordering, and the
    bug we're fixing is exactly the kind of subtle cross-handler
    coordination problem we want to avoid betting on.

    - MOVE: anything over ~10px of finger drift is a swipe, not a
      tap. 10px is the de facto industry value (matches Material
      and iOS HIG tap slop).
    - TIME: gestures longer than 600ms are also disqualified — that
      catches the "long-press to summon context menu" / "tap and
      hold for hover preview" cases that aren't really taps either.
  */
  const TAP_MOVE_THRESHOLD = 10;
  const TAP_TIME_THRESHOLD = 600;

  /*
    Active gesture state, or null between gestures. Recording the
    card at pointerdown rather than relying on the pointerup target
    matters because the touch point may slide slightly across the
    card's edge — we want to activate the card the finger originally
    landed on, not whatever happened to be under it at release.
  */
  let activeGesture = null;

  /*
    Brief flag set right after pointerup so the synthesized click
    that browsers fire after a touch / mouse sequence doesn't
    double-trigger the modal. Keyboard-only clicks (Enter / Space
    on the focused card button) have no preceding pointer events,
    so they bypass this flag and reach the click handler.
  */
  let recentPointerActivity = false;

  function findTargetSection(triggerEl) {
    const targetId = triggerEl.getAttribute("data-target-section-id") || "";
    if (targetId) {
      const explicit = document.querySelector(
        `${SECTION_TAG}[data-section-id="${CSS.escape(targetId)}"]`,
      );
      if (explicit) return explicit;
    }
    return document.querySelector(SECTION_TAG);
  }

  function openCardModal(card, e) {
    const trigger = card.closest(TRIGGER_SELECTOR);
    if (!trigger) return;
    const idx = parseInt(card.getAttribute("data-slide-index"), 10);
    if (Number.isNaN(idx)) return;

    const target = findTargetSection(trigger);
    if (!target) {
      console.warn(
        "[influencer-reels-trigger] No <influencer-video-reels> on page; cannot open modal.",
      );
      return;
    }

    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    target.dispatchEvent(
      new CustomEvent("influencer-reels:open", { detail: { index: idx } }),
    );
  }

  function onPointerDown(e) {
    const card = e.target.closest(CARD_SELECTOR);
    if (!card) {
      activeGesture = null;
      return;
    }
    // Ignore right-click and middle-click on mouse — same convention as the
    // section's own card handler.
    if (e.pointerType === "mouse" && e.button !== 0) {
      activeGesture = null;
      return;
    }
    activeGesture = {
      x: e.clientX,
      y: e.clientY,
      time: performance.now(),
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      card: card,
      cancelled: false,
    };
  }

  function onPointerMove(e) {
    if (!activeGesture) return;
    if (e.pointerId !== activeGesture.pointerId) return;
    const dx = Math.abs(e.clientX - activeGesture.x);
    const dy = Math.abs(e.clientY - activeGesture.y);
    if (dx > TAP_MOVE_THRESHOLD || dy > TAP_MOVE_THRESHOLD) {
      // Beyond the slop radius — this is a swipe. Cancel the pending
      // tap so pointerup doesn't open the modal; Swiper will handle
      // the rest of the gesture as normal carousel navigation.
      activeGesture.cancelled = true;
    }
  }

  function onPointerUp(e) {
    const g = activeGesture;
    activeGesture = null;
    if (!g || g.cancelled) return;
    if (e.pointerId !== g.pointerId) return;
    if (performance.now() - g.time > TAP_TIME_THRESHOLD) return;

    // Mark pointer activity so the trailing synthesized click event
    // gets ignored by onClick (we already handled this gesture here).
    recentPointerActivity = true;
    setTimeout(() => {
      recentPointerActivity = false;
    }, 100);

    openCardModal(g.card, e);
  }

  function onPointerCancel() {
    activeGesture = null;
  }

  function onClick(e) {
    // Suppress the synthesized click that follows every touch / mouse
    // gesture we already processed in onPointerUp. Keyboard
    // activation (Enter / Space on a focused button) doesn't go
    // through pointerdown / pointerup, so it skips this guard and
    // continues into openCardModal below.
    if (recentPointerActivity) return;
    const card = e.target.closest(CARD_SELECTOR);
    if (!card) return;
    openCardModal(card, e);
  }

  // Pointer events must be non-passive only where we want to be free to
  // preventDefault if needed. The gating logic itself doesn't preventDefault
  // on pointermove (Swiper's scrolling depends on browser-default behavior),
  // so we mark those listeners passive for performance and let Swiper drive.
  document.addEventListener("pointerdown", onPointerDown, true);
  document.addEventListener("pointermove", onPointerMove, { passive: true, capture: true });
  document.addEventListener("pointerup", onPointerUp, true);
  document.addEventListener("pointercancel", onPointerCancel, true);
  document.addEventListener("click", onClick, true);
})();
